import { onMounted, ref } from 'vue';

/**
 * Current version of the "What's New" content.
 * Increment this when new content is added to trigger the popup for all users.
 */
const WHATS_NEW_VERSION = '3.0.0';
const STORAGE_KEY = 'whats-new-seen-version';

/**
 * Composable to manage "What's New" popup visibility.
 * Shows the popup automatically on first visit after a new release.
 * Tracks seen state in localStorage to prevent repeat popups.
 */
export function useWhatsNew() {
   const shouldShowPopup = ref(false);
   const isOpen = ref(false);

   /**
    * Check if the user has seen the current version
    */
   function hasSeenCurrentVersion(): boolean {
      const seenVersion = localStorage.getItem(STORAGE_KEY);
      return seenVersion === WHATS_NEW_VERSION;
   }

   /**
    * Mark the current version as seen
    */
   function markAsSeen(): void {
      localStorage.setItem(STORAGE_KEY, WHATS_NEW_VERSION);
   }

   /**
    * Open the What's New popup
    */
   function open(): void {
      isOpen.value = true;
   }

   /**
    * Close the What's New popup and mark as seen
    */
   function close(): void {
      isOpen.value = false;
      markAsSeen();
   }

   /**
    * Check on mount if we should show the popup
    */
   onMounted(() => {
      if (!hasSeenCurrentVersion()) {
         shouldShowPopup.value = true;
         isOpen.value = true;
      }
   });

   return {
      isOpen,
      shouldShowPopup,
      open,
      close,
      markAsSeen,
      hasSeenCurrentVersion,
      currentVersion: WHATS_NEW_VERSION,
   };
}
